import { type EnrichedAuditEvent } from '@eppendorf/vnls-audit-event-client';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { fetcher } from '$shared/fetch';
import { AUDIT_BASE_PATH, createPathWithQueryParams } from '$shared/utils/api-paths';

import { AuditEventCategory } from '$features/audit-events/components/filters/category-filter';

export const auditEventsQueryKey = 'audit-events';
export const auditEventDetailQueryKey = 'audit-event-detail';

export const createAuditEventsQuery = (
  from: string | null,
  to: string | null,
  searchTerm: string | null,
  category: AuditEventCategory | null,
) => ({
  queryKey: [auditEventsQueryKey, from, to, searchTerm, category],
  queryFn: ({ pageParam }: { pageParam: EnrichedAuditEvent | undefined }) =>
    fetcher.get<EnrichedAuditEvent[]>(
      createPathWithQueryParams(AUDIT_BASE_PATH, {
        pageSize: 10,
        ...(from !== undefined && { from }),
        ...(to !== undefined && { to }),
        ...(searchTerm !== undefined && { searchTerm }),
        ...(category !== AuditEventCategory.All &&
          category !== undefined && { category }),
        ...(pageParam && {
          searchAfterTimestampIso: pageParam.timestamp,
        }),
      }),
    ),
  getNextPageParam: (lastPage: EnrichedAuditEvent[], _pages: EnrichedAuditEvent[][]) => {
    if (lastPage.length) {
      return lastPage.at(-1);
    }

    return undefined;
  },
  initialPageParam: undefined,
  staleTime: Infinity,
});

export const useAuditEventsQuery = (
  from: string | null,
  to: string | null,
  searchTerm: string | null,
  category: AuditEventCategory | null,
) => useInfiniteQuery(createAuditEventsQuery(from, to, searchTerm, category));

export const createAuditEventDetailQuery = (id: string) => ({
  queryKey: [auditEventDetailQueryKey, id],
  queryFn: () => fetcher.get<EnrichedAuditEvent>(`${AUDIT_BASE_PATH}/${id}`),
  staleTime: Infinity,
  placeholderData: keepPreviousData,
});

export const useAuditEventDetailQuery = (id: string) =>
  useQuery({
    ...createAuditEventDetailQuery(id),
    enabled: !!id, // closing the sidecar should not trigger a query
  });
